import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { isNetworkError } from '../../utils/api/networkErrors';
import type { Database } from '../../types/database';

type Profile = Database['public']['Tables']['user_profiles']['Row'];

export function useProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000;

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setIsLoading(false);
      setError(null);
      setRetryCount(0);
      return;
    }

    const loadProfile = async () => {
      try {
        // Initial delay to allow profile creation
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if user is admin first
        const { data: adminCheck, error: adminError } = await supabase
          .rpc('is_admin', { user_id: user.id });

        if (adminError) throw adminError;

        // Try up to 3 times with exponential backoff
        for (let i = 0; i < MAX_RETRIES; i++) {
          try {
            const { data: profile, error } = await supabase
              .from('user_profiles')
              .select('*')
              .eq('id', user.id)
              .maybeSingle();

            if (error) {
              if (error.code === 'PGRST116' || isNetworkError(error)) {
                // Wait longer between each retry
                await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, i)));
                continue;
              }
              throw error;
            }

            if (!profile) {
              // Create profile if it doesn't exist
              const { data: newProfile, error: createError } = await supabase
                .from('user_profiles')
                .insert({
                  id: user.id,
                  first_name: user.user_metadata?.first_name || '',
                  last_name: user.user_metadata?.last_name || '',
                  is_admin: false
                })
                .select()
                .single();

              if (createError) throw createError;
              setProfile(newProfile);
            } else {
              setProfile(profile);
            }

            setError(null);
            setRetryCount(0);
            break;
          } catch (retryError) {
            if (i === MAX_RETRIES - 1) throw retryError;
            continue;
          }
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        if (isNetworkError(err) && retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to load profile'));
        if (!isNetworkError(err)) {
          toast.error('Failed to load profile');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user, retryCount]);

  const update = async (updates: Partial<Profile>) => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const { data: updated, error } = await supabase
        .from('user_profiles')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(updated);
      setError(null);
      toast.success('Profile updated successfully');
      return updated;
    } catch (err) {
      console.error('Error updating profile:', err);
      const error = err instanceof Error ? err : new Error('Failed to update profile');
      setError(error);
      if (isNetworkError(err)) {
        handleNetworkError(err, () => update(updates));
      } else {
        toast.error('Failed to update profile');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { profile, isLoading, error, update };
}